import axios from "axios";
import projectJson from "./local.json";
import { useEditorState } from "@/store/editorState";
import { getPublicProject, isPublicProject } from "./utils";

// Production editor hosts use VITE_API_URL (EU build: editor lambda, US build: editor-lambda-us);
// every other host (staging, localhost) uses the staging api.
const PRODUCTION_HOSTS = ["editor.riders.ai", "editor-us.riders.ai"];
const API_URL = PRODUCTION_HOSTS.includes(window.location.hostname)
  ? import.meta.env.VITE_API_URL
  : import.meta.env.VITE_API_STAGING_URL;

const isLocal = window.location.hostname === "localhost";

export const restartSession = async (token: string) => {
  return new Promise<any>(async (resolve, reject) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/restart`, {
        method: "get",
        headers: {
          "Content-Type": "application/json",
          authorization: `Token ${token}`,
        },
      });

      resolve(data.accessToken);
    } catch (error) {
      reject(error);
    }
  });
};

export const collectAchievement = async (
  token: string,
  achievementId: string,
) => {
  return new Promise<any>(async (resolve, reject) => {
    try {
      const { data } = await axios.post(
        API_URL + "/api/achievements/achieve/",
        {
          data: achievementId,
        },
        {
          headers: {
            authorization: `Token ${token}`,
          },
        },
      );
      resolve(data);
    } catch (error) {
      reject(error);
    }
  });
};

export const getAchievements = async (token: string, language: string) => {
  return new Promise<any>(async (resolve, reject) => {
    try {
      if (useEditorState.getState().environment === "local" || isPublicProject()) {
        resolve([]);
        return;
      }

      const { data } = await axios.get(API_URL + "/api/achievements/list", {
        headers: {
          authorization: `Token ${token}`,
          "Accept-Language": language || "en",
        },
      });
      resolve(data);
    } catch (error) {
      reject(error);
    }
  });
};

export const getScore = async (token: string, results: any) => {
  return new Promise(async (resolve, reject) => {
    try {
      await axios.post(
        API_URL + "/api/score",
        {
          data: results,
        },
        {
          headers: {
            authorization: `Token ${token}`,
          },
        },
      );
      resolve(true);
    } catch (error) {
      reject(error);
    }
  });
};

export const setQuizResult = async (
  token: string,
  quiz: string | number,
  results: any,
) => {
  return new Promise(async (resolve, reject) => {
    try {
      const res = await axios.post(
        API_URL + `/api/quiz/${quiz}`,
        {
          data: results,
        },
        {
          headers: {
            authorization: `Token ${token}`,
          },
        },
      );
      resolve(res);
    } catch (error) {
      reject(error);
    }
  });
};

export const getQuizResults = async (token: string, quiz: string | number) => {
  return new Promise(async (resolve, _reject) => {
    try {
      if (useEditorState.getState().platform === "lti") {
        return resolve({ data: useEditorState.getState().ltiQuizResults[String(quiz)] || {} });
      }

      const res = await axios.get(API_URL + `/api/quiz/${quiz}`, {
        headers: {
          authorization: `Token ${token}`,
        },
      });
      resolve(res);
    } catch (_error) {
      resolve([]);
    }
  });
};

export const getQuizQuestions = async (
  token: string,
  quiz: string | number,
  language: string,
) => {
  return new Promise(async (resolve, reject) => {
    try {
      const res = await axios.get(
        API_URL + `/api/quiz/${quiz}/questions`,
        {
          headers: {
            authorization: `Token ${token}`,
            "Accept-Language": language || "en",
          },
        },
      );
      resolve(res);
    } catch (error) {
      reject(error);
    }
  });
};

export const getLTIQuizzesList = async (token: string) => {
  return new Promise(async (resolve, reject) => {
    try {
      if (useEditorState.getState().platform !== "lti") {
        return reject([]);
      }

      const res = await axios.get(API_URL + `/api/quiz/list`, {
        headers: {
          authorization: `Token ${token}`,
        },
      });
      resolve(res.data);
    } catch (error) {
      reject(error);
    }
  });
};

export const publishBranch = async (token: string, message: string) => {
  return new Promise(async (resolve, reject) => {
    try {
      await axios.post(
        API_URL + "/api/branches/publish",
        {
          message: message,
        },
        {
          headers: {
            Authorization: `Token ${token}`,
          },
        },
      );
      resolve(true);
    } catch (error) {
      reject(error);
    }
  });
};

export const notifyStudent = async (
  token: string,
  data: {
    classroom: string;
    courseSlug: string;
  },
) => {
  return new Promise(async (resolve, reject) => {
    try {
      await axios.post(API_URL + "/api/branches/notify", data, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });
      resolve(true);
    } catch (error) {
      reject(error);
    }
  });
};

export const publishBranchWithRequest = async (
  token: string,
  message: string,
  hasRequest: boolean,
) => {
  return new Promise(async (resolve, reject) => {
    try {
      await axios.post(
        API_URL +
        "/api/branches/publish-with-request?has_request=" +
        hasRequest,
        {
          message: message,
        },
        {
          headers: {
            Authorization: `Token ${token}`,
          },
        },
      );
      resolve(true);
    } catch (error) {
      reject(error);
    }
  });
};

export const verifyEdusimToken = async (token: string) => {
  return new Promise(async (resolve, reject) => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/auth/verify/edusim?token=${token}`,
      );
      resolve(data);
    } catch (error) {
      reject(error);
    }
  });
};

export const verifyToken = async (token: string) => {
  // Local development playground
  if (token === "playground" && isLocal) {
    return projectJson;
  }

  // Public project
  if (isPublicProject()) {
    return getPublicProject();
  }


  // First check if session is valid.
  const { data } = await axios.get(
    `${API_URL}/api/auth/verify?token=${token}`,
  );

  return data
};



export const idleShutDown = async (token: string) => {
  return new Promise(async (resolve, reject) => {
    try {
      await axios.get(`${API_URL}/api/auth/idle`, {
        headers: {
          "Content-Type": "application/json",
          authorization: `Token ${token}`,
        },
      });
      resolve(true);
    } catch (error) {
      reject(error);
    }
  });
};

export const storagePush = async (
  token: string,
  file: string,
  content: string | null,
) => {
  return new Promise(async (resolve, reject) => {
    try {
      await axios.post(
        `${API_URL}/api/storage/push`,
        {
          filePath: file,
          fileContent: content,
        },
        {
          headers: {
            Authorization: `Token ${token}`,
          },
        },
      );
      resolve(true);
    } catch (error) {
      reject(error);
    }
  });
};

export const getActiveProxyServerAddress = async () => {
  return new Promise(async (resolve, reject) => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/settings/active-proxy-server`,
      );
      resolve(data.value);
    } catch (error) {
      reject(error);
    }
  });
};

export const pushFileToS3 = async (
  token: string,
  file: string,
  content: string,
  type: string,
) => {
  return new Promise(async (resolve, reject) => {
    try {
      const { data } = await axios.post(
        `${"https://gtkb2pw9xg.execute-api.eu-west-1.amazonaws.com"}/api/aws/write`,
        {
          path: file,
          body: content,
          contentType: type,
        },
        {
          headers: {
            Authorization: token,
          },
        },
      );
      resolve(data.value);
    } catch (error) {
      reject(error);
    }
  });
};

export const setContentId = async (token: string, contentId: string) => {
  return new Promise(async (resolve, reject) => {
    try {
      const res = await axios.post(
        API_URL + "/api/auth/set-content-id",
        {
          content_id: contentId,
        },
        {
          headers: {
            authorization: `Token ${token}`,
          },
        },
      );
      resolve(res);
    } catch (error) {
      reject(error);
    }
  });
};

export const getModuleProgresses = async (token: string) => {
  return new Promise<any>(async (resolve, reject) => {
    try {
      if (useEditorState.getState().environment === "local" || isPublicProject()) {
        resolve([]);
        return;
      }
      const { data } = await axios.get(API_URL + "/api/module-progresses", {
        headers: {
          authorization: `Token ${token}`,
        },
      });
      resolve(data);
    } catch (error) {
      reject(error);
    }
  });
};

export const submitModuleProgress = async (token: string, title: string) => {
  return new Promise<any>(async (resolve, reject) => {
    try {
      if (useEditorState.getState().environment === "local" || isPublicProject()) {
        resolve(true);
        return;
      }
      const { data } = await axios.post(
        API_URL + "/api/module-progresses/",
        {
          data: title,
        },
        {
          headers: {
            authorization: `Token ${token}`,
          },
        },
      );
      resolve(data);
    } catch (error) {
      reject(error);
    }
  });
};

export const notificationsList = async (
  token: string,
  page: number,
  page_size: number,
  language: string,
) => {
  return new Promise<any>(async (resolve, reject) => {
    try {
      if (useEditorState.getState().environment === "local" || isPublicProject()) {
        resolve([]);
        return;
      }
      const { data } = await axios.get(
        API_URL +
        `/api/notifications?page=${page}&page_size=${page_size}&language=${language}`,
        {
          headers: {
            authorization: `Token ${token}`,
          },
        },
      );
      resolve(data);
    } catch (error) {
      reject(error);
    }
  });
};

export const notificationsMarkAsRead = async (
  token: string,
  encryptedData: string,
) => {
  return new Promise<any>(async (resolve, reject) => {
    try {
      const { data } = await axios.post(
        API_URL + "/api/notifications/mark_as_read",
        {
          data: encryptedData,
        },
        {
          headers: {
            authorization: `Token ${token}`,
          },
        },
      );
      resolve(data);
    } catch (error) {
      reject(error);
    }
  });
};

export const notificationsUnreadCount = async (token: string) => {
  return new Promise<any>(async (resolve, reject) => {
    try {
      if (useEditorState.getState().environment === "local" || isPublicProject()) {
        resolve(0);
        return;
      }
      const { data } = await axios.get(
        API_URL + "/api/notifications/unread_count",
        {
          headers: {
            authorization: `Token ${token}`,
          },
        },
      );
      resolve(data);
    } catch (error) {
      reject(error);
    }
  });
};

export const getYaml = async (contentId: string) => {
  const { data } = await axios.get(
    `https://sim.riders.ai/projects/${contentId}/layouts.yaml`,
    {
      headers: {
        "Content-Type": "application/json",
      },
    },
  );
  return data;
}

export const getConfigJson = async (contentId: string) => {
  const { data } = await axios.get(
    `https://sim.riders.ai/config/${contentId}.json`,
    {
      headers: {
        "Content-Type": "application/json",
      },
    },
  );
  return data;
}

export const getStemboxBranches = async () => {
  // Data format received:
  // [ "FLL-mockup", "app_v6" ]
  const { data } = await axios.get(
    `${API_URL}/api/listStemboxBranches`,
    {
      headers: {
        "Content-Type": "application/json",
      },
    },
  );
  return data;
}
